<template>
  <AppLayout>
    <div class="transaction fleet-module-page">
      <section class="container">

        <div class="fleet-stat-grid">
          <div class="fleet-stat-card"><span>Total</span><strong>{{ stats.total }}</strong></div>
          <div class="fleet-stat-card"><span>Pending</span><strong>{{ stats.pending }}</strong></div>
          <div class="fleet-stat-card"><span>Approved</span><strong>{{ stats.approved }}</strong></div>
          <div class="fleet-stat-card"><span>Completed</span><strong>{{ stats.completed }}</strong></div>
        </div>

        <div class="filter-search">
          <div class="filter-search-content">
            <div class="row">
              <div class="col-md-4">
                <div class="form-group">
                  <label>Vehicle</label>
                  <select v-model="filters.vehicleId" class="form-control">
                    <option value="">All Vehicles</option>
                    <option v-for="v in vehicles" :key="v.id" :value="String(v.id)">{{ vehicleLabel(v) }}</option>
                  </select>
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group">
                  <label>Status</label>
                  <select v-model="filters.status" class="form-control">
                    <option value="">All Statuses</option>
                    <option v-for="s in statuses" :key="s" :value="s">{{ s }}</option>
                  </select>
                </div>
              </div>
              <div class="col-md-4 d-flex align-items-end">
                <button class="btn btn-sm btn-style-2 w-100" @click="loadData">Refresh</button>
              </div>
            </div>
          </div>
        </div>

        <div class="table-toolbar">
          <button class="btn btn-style-2 btn-sm" @click="openCreate">Schedule Maintenance</button>
        </div>

        <div class="data-table-wrapper">
          <div class="table-wrap">
            <table class="fleet-table table table-hover">
              <thead>
                <tr>
                  <th>No.</th>
                  <th>Vehicle</th>
                  <th>Type</th>
                  <th>Scheduled Date</th>
                  <th>Status</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading"><td colspan="7" class="text-center py-3">Loading...</td></tr>
                <tr v-else-if="records.length === 0"><td colspan="7" class="text-center py-3 text-muted">No records found</td></tr>
                <tr v-for="(r, i) in records" :key="r.id" v-else>
                  <td>{{ i + 1 }}</td>
                  <td>{{ vehicleLabel(r.vehicle) }}</td>
                  <td>{{ r.maintenanceType?.name || '-' }}</td>
                  <td>{{ fmtDate(r.scheduledDate) }}</td>
                  <td><span class="status-pill" :class="statusClass(r.status)">{{ r.status }}</span></td>
                  <td>{{ r.notes || '-' }}</td>
                  <td>
                    <div class="table-actions">
                      <button v-if="r.status === 'PENDING'" class="mini-btn success" @click="doApprove(r)">Approve</button>
                      <button v-if="r.status === 'PENDING'" class="mini-btn danger" @click="openReject(r)">Reject</button>
                      <button v-if="r.status === 'APPROVED'" class="mini-btn" @click="doAllocate(r)">Allocate</button>
                      <button v-if="r.status === 'ALLOCATED'" class="mini-btn success" @click="openComplete(r)">Complete</button>
                      <button v-if="['PENDING','REJECTED'].includes(r.status)" class="mini-btn danger" @click="doDelete(r)">Delete</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Pagination -->
        <div class="d-flex justify-content-end align-items-center gap-2 mt-2" v-if="total > limit">
          <button class="btn btn-sm btn-style-2" :disabled="page <= 1" @click="page--; loadData()">Prev</button>
          <span style="font-size:13px;">Page {{ page }} / {{ Math.ceil(total / limit) }}</span>
          <button class="btn btn-sm btn-style-2" :disabled="page >= Math.ceil(total / limit)" @click="page++; loadData()">Next</button>
        </div>

        <!-- Create Modal -->
        <div v-if="showCreate" class="svc-modal-overlay" @click.self="showCreate = false">
          <div class="svc-modal-box">
            <div class="svc-modal-header">
              <h6 class="fw-bold mb-0">Schedule Maintenance</h6>
              <button class="btn-close-plain" @click="showCreate = false">&times;</button>
            </div>
            <div class="svc-modal-body">
              <div class="row g-2">
                <div class="col-12">
                  <label class="form-label" style="font-size:11px;">Vehicle <span class="text-danger">*</span></label>
                  <select v-model.number="form.vehicleId" class="form-control form-control-sm">
                    <option :value="null">Select vehicle</option>
                    <option v-for="v in vehicles" :key="v.id" :value="v.id">{{ vehicleLabel(v) }}</option>
                  </select>
                </div>
                <div class="col-12">
                  <label class="form-label" style="font-size:11px;">Maintenance Type <span class="text-danger">*</span></label>
                  <select v-model.number="form.maintenanceTypeId" class="form-control form-control-sm">
                    <option :value="null">Select type</option>
                    <option v-for="t in maintenanceTypes" :key="t.id" :value="t.id">{{ t.name }}</option>
                  </select>
                </div>
                <div class="col-12">
                  <label class="form-label" style="font-size:11px;">Scheduled Date <span class="text-danger">*</span></label>
                  <input v-model="form.scheduledDate" type="date" class="form-control form-control-sm" />
                </div>
                <div class="col-12">
                  <label class="form-label" style="font-size:11px;">Notes</label>
                  <textarea v-model="form.notes" class="form-control form-control-sm" rows="2"></textarea>
                </div>
              </div>
              <p v-if="formError" class="text-danger small mt-2">{{ formError }}</p>
            </div>
            <div class="svc-modal-footer">
              <button class="btn btn-sm btn-style-2" @click="showCreate = false">Cancel</button>
              <button class="btn btn-sm btn-primary" @click="saveCreate" :disabled="saving">
                <span v-if="saving">Saving...</span>
                <span v-else>Save</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Reject Modal -->
        <div v-if="showReject" class="svc-modal-overlay" @click.self="showReject = false">
          <div class="svc-modal-box" style="max-width:400px;">
            <div class="svc-modal-header">
              <h6 class="fw-bold mb-0">Reject Maintenance</h6>
              <button class="btn-close-plain" @click="showReject = false">&times;</button>
            </div>
            <div class="svc-modal-body">
              <label class="form-label" style="font-size:11px;">Reason (optional)</label>
              <textarea v-model="rejectNotes" class="form-control form-control-sm" rows="3" placeholder="Enter rejection reason..."></textarea>
            </div>
            <div class="svc-modal-footer">
              <button class="btn btn-sm btn-style-2" @click="showReject = false">Cancel</button>
              <button class="btn btn-sm btn-danger" @click="doReject" :disabled="saving">
                <span v-if="saving">Rejecting...</span>
                <span v-else>Reject</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Complete Modal -->
        <div v-if="showComplete" class="svc-modal-overlay" @click.self="showComplete = false">
          <div class="svc-modal-box" style="max-width:400px;">
            <div class="svc-modal-header">
              <h6 class="fw-bold mb-0">Complete Maintenance</h6>
              <button class="btn-close-plain" @click="showComplete = false">&times;</button>
            </div>
            <div class="svc-modal-body">
              <label class="form-label" style="font-size:11px;">Completion Notes (optional)</label>
              <textarea v-model="completeNotes" class="form-control form-control-sm" rows="3" placeholder="Enter completion notes..."></textarea>
            </div>
            <div class="svc-modal-footer">
              <button class="btn btn-sm btn-style-2" @click="showComplete = false">Cancel</button>
              <button class="btn btn-sm btn-success" @click="doComplete" :disabled="saving">
                <span v-if="saving">Saving...</span>
                <span v-else>Mark Complete</span>
              </button>
            </div>
          </div>
        </div>

      </section>
    </div>
  </AppLayout>
</template>

<script>
import AppLayout from '@/components/AppLayout.vue';
import fleetApi from '@/api/fleet';
import Swal from '@/utils/swal';

const emptyForm = () => ({ vehicleId: null, maintenanceTypeId: null, scheduledDate: '', notes: '' });

export default {
  name: 'FleetMaintenance',
  components: { AppLayout },
  data() {
    return {
      records: [],
      vehicles: [],
      maintenanceTypes: [],
      loading: false,
      saving: false,
      total: 0,
      page: 1,
      limit: 20,
      filters: { vehicleId: '', status: '' },
      statuses: ['PENDING', 'APPROVED', 'ALLOCATED', 'REJECTED', 'COMPLETED'],
      showCreate: false,
      form: emptyForm(),
      formError: '',
      showReject: false,
      rejectNotes: '',
      showComplete: false,
      completeNotes: '',
      actionTarget: null,
    };
  },
  computed: {
    stats() {
      return {
        total: this.total,
        pending: this.records.filter(r => r.status === 'PENDING').length,
        approved: this.records.filter(r => r.status === 'APPROVED').length,
        completed: this.records.filter(r => r.status === 'COMPLETED').length,
      };
    },
  },
  mounted() {
    this.loadData();
    this.loadLookups();
  },
  methods: {
    async loadLookups() {
      const [vehiclesRes, typesRes] = await Promise.all([
        fleetApi.listVehicles({ page: 1, limit: 200 }),
        fleetApi.listMaintenanceTypes(),
      ]);
      this.vehicles = fleetApi.unwrapList(vehiclesRes);
      this.maintenanceTypes = fleetApi.unwrapList(typesRes);
    },
    async loadData() {
      this.loading = true;
      try {
        const params = { page: this.page, limit: this.limit };
        if (this.filters.vehicleId) params.vehicleId = this.filters.vehicleId;
        if (this.filters.status) params.status = this.filters.status;
        const res = await fleetApi.listScheduledMaintenances(params);
        this.records = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        this.total = res?.total ?? this.records.length;
      } catch (err) {
        this.$toast?.error?.(err?.message || 'Failed to load records');
      } finally {
        this.loading = false;
      }
    },
    vehicleLabel(v) {
      if (!v) return '-';
      const d = [v.make, v.model].filter(Boolean).join(' ');
      return d ? `${v.plateNumber} — ${d}` : (v.plateNumber || `Vehicle #${v.id}`);
    },
    fmtDate(v) {
      if (!v) return '-';
      return new Date(v).toLocaleDateString();
    },
    statusClass(s) {
      return {
        PENDING: 'status-default',
        APPROVED: 'status-valid',
        ALLOCATED: 'status-expiring',
        COMPLETED: 'status-valid',
        REJECTED: 'status-expired',
      }[s] || 'status-default';
    },
    openCreate() {
      this.form = emptyForm();
      this.formError = '';
      this.showCreate = true;
    },
    async saveCreate() {
      if (!this.form.vehicleId || !this.form.maintenanceTypeId || !this.form.scheduledDate) {
        this.formError = 'Vehicle, type and date are required.';
        return;
      }
      this.saving = true;
      try {
        await fleetApi.createScheduledMaintenance({
          vehicleId: this.form.vehicleId,
          maintenanceTypeId: this.form.maintenanceTypeId,
          scheduledDate: this.form.scheduledDate,
          notes: this.form.notes || undefined,
        });
        this.$toast?.success?.('Maintenance scheduled');
        this.showCreate = false;
        await this.loadData();
      } catch (err) {
        this.formError = err?.message || 'Failed to create';
      } finally {
        this.saving = false;
      }
    },
    async doApprove(r) {
      try {
        await fleetApi.approveScheduledMaintenance(r.id);
        this.$toast?.success?.('Approved');
        await this.loadData();
      } catch (err) {
        this.$toast?.error?.(err?.message || 'Failed to approve');
      }
    },
    openReject(r) {
      this.actionTarget = r;
      this.rejectNotes = '';
      this.showReject = true;
    },
    async doReject() {
      this.saving = true;
      try {
        await fleetApi.rejectScheduledMaintenance(this.actionTarget.id, this.rejectNotes);
        this.$toast?.success?.('Rejected');
        this.showReject = false;
        await this.loadData();
      } catch (err) {
        this.$toast?.error?.(err?.message || 'Failed to reject');
      } finally {
        this.saving = false;
      }
    },
    async doAllocate(r) {
      try {
        await fleetApi.allocateScheduledMaintenance(r.id);
        this.$toast?.success?.('Allocated');
        await this.loadData();
      } catch (err) {
        this.$toast?.error?.(err?.message || 'Failed to allocate');
      }
    },
    openComplete(r) {
      this.actionTarget = r;
      this.completeNotes = '';
      this.showComplete = true;
    },
    async doComplete() {
      this.saving = true;
      try {
        await fleetApi.completeScheduledMaintenance(this.actionTarget.id, this.completeNotes);
        this.$toast?.success?.('Marked complete');
        this.showComplete = false;
        await this.loadData();
      } catch (err) {
        this.$toast?.error?.(err?.message || 'Failed to complete');
      } finally {
        this.saving = false;
      }
    },
    async doDelete(r) {
      const result = await Swal.fire({
        title: 'Delete scheduled maintenance?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!',
      });
      if (!result.isConfirmed) return;
      try {
        await fleetApi.deleteScheduledMaintenance(r.id);
        this.$toast?.success?.('Deleted');
        await this.loadData();
      } catch (err) {
        this.$toast?.error?.(err?.message || 'Failed to delete');
      }
    },
  },
};
</script>

<style scoped>
@import './fleet.shared.css';

.mini-btn.success {
  background: #198754;
  color: #fff;
  border-color: #198754;
}
.mini-btn.success:hover {
  background: #157347;
}
</style>
